// src/pdf/pdfCore.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export function createPdfFromSpec(spec) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const marginL = spec?.layout?.marginLeft ?? 40;
  const marginR = spec?.layout?.marginRight ?? 40;
  let y = spec?.layout?.startY ?? 50;

  // Header
  if (spec.header?.logoDataUrl) {
    const w = spec.header.logoWidthPt ?? 64;
    const h = spec.header.logoHeightPt ?? 32;
    doc.addImage(spec.header.logoDataUrl, spec.header.logoFormat ?? "PNG", pageW - marginR - w, 24, w, h);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(spec.title || "Report", marginL, y);
  y += 18;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  (spec.subtitles || []).forEach((line) => {
    doc.text(String(line), marginL, y);
    y += 14;
  });

  y += 10;

  // Sections
  for (const sec of spec.sections || []) {
    if (y > pageH - 80) {
      doc.addPage();
      y = 40;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(sec.title, marginL, y);
    y += 10;

    if (sec.table?.rows?.length) {
      const body = sec.table.rows.map((row) => row.map((c) => (c == null || c === "" ? "—" : String(c))));
      autoTable(doc, {
        body,
        startY: y,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 4, halign: "left", valign: "middle" },
        margin: { left: marginL, right: marginR },
        headStyles: { fillColor: [240, 240, 240] },
      });
      y = (doc.lastAutoTable?.finalY ?? y) + 16;
    }

    if (sec.text) {
      const txt = String(sec.text);
      const lines = doc.splitTextToSize(txt, pageW - marginL - marginR);
      const boxPad = 10;
      const lineH = 14;
      const boxH = Math.max(40, lines.length * lineH + boxPad * 2);

      if (y + boxH > pageH - 40) {
        doc.addPage();
        y = 40;
      }

      if (sec.textStyle?.borderColor) {
        doc.setDrawColor(...sec.textStyle.borderColor);
      } else {
        doc.setDrawColor(0, 0, 0);
      }

      if (sec.textStyle?.textColor) {
        doc.setTextColor(...sec.textStyle.textColor);
      } else {
        doc.setTextColor(0, 0, 0);
      }

      doc.roundedRect(marginL, y, pageW - marginL - marginR, boxH, 4, 4);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      let ty = y + boxPad + 10;
      lines.forEach((ln) => {
        doc.text(ln, marginL + boxPad, ty);
        ty += lineH;
      });

      // reset
      doc.setDrawColor(0, 0, 0);
      doc.setTextColor(0, 0, 0);

      y += boxH + 16;
    }

    // Images grid
    if (sec.images?.length) {
      const cols = sec.imagesGrid?.cols ?? 3;
      const size = sec.imagesGrid?.size ?? 150;
      const gap = sec.imagesGrid?.gap ?? 10;

      if (y > pageH - 80) {
        doc.addPage();
        y = 40;
      }

      let x = marginL;
      let col = 0;

      for (const img of sec.images) {
        if (y + size + 24 > pageH - 20) {
          doc.addPage();
          y = 40;
          x = marginL;
          col = 0;
        }

        try {
          doc.addImage(img.dataUrl, img.format ?? "JPEG", x, y, size, size, undefined, "FAST");
        } catch (e) {
          // ignore image failures; keep PDF alive
        }

        doc.setFontSize(8);
        doc.text((img.label || "").slice(0, 30), x, y + size + 10, { maxWidth: size });

        col++;
        if (col >= cols) {
          col = 0;
          x = marginL;
          y += size + 24;
        } else {
          x += size + gap;
        }
      }

      y += 10;
    }
  }

  return doc;
}

export async function downloadPdfFromSpec(spec) {
  const doc = createPdfFromSpec(spec);
  doc.save(spec.fileName || "report.pdf");
}
